<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Button from "../../components/input/Button.svelte";
  import ConfirmDialog from "../../components/overlay/ConfirmDialog.svelte";
  import EmptyState from "../../components/feedback/EmptyState.svelte";
  import { formatCurrency } from "../../lib/utils/format";

  type LocalTransport = {
    id: string;
    name: string;
    transport_type: string;
    price_cents?: number;
    balance_cents?: number;
    currency?: string;
    valid_from?: string;
    valid_until?: string;
    purchased: boolean;
    is_active_today: boolean;
  };

  let { trip, transports = [] }: { trip: any; transports?: LocalTransport[] } = $props();

  const TYPE_LABELS: Record<string, string> = {
    ic_card: "💳 IC 카드",
    pass: "🎫 패스",
    ticket: "🎟️ 티켓",
    rental_bike: "🚲 렌탈 자전거",
    bus: "🚌 버스",
    subway: "🚇 지하철",
    tram: "🚊 트램",
    other: "🚐 기타",
  };

  let localTransports = $state<LocalTransport[]>([]);
  let showForm = $state(false);
  let submitting = $state(false);
  let balanceItem = $state<LocalTransport | null>(null);
  let newBalance = $state("");
  let confirmDeleteId = $state<string | null>(null);
  let confirmOpen = $state(false);
  let transportsHydrated = false;

  let form = $state({
    name: "",
    transport_type: "ic_card",
    price_cents: "",
    balance_cents: "",
    valid_from: "",
    valid_until: "",
  });

  const plannedItems = $derived(localTransports.filter((t) => !t.purchased));
  const purchasedItems = $derived(localTransports.filter((t) => t.purchased));

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  $effect(() => {
    if (transportsHydrated) return;
    localTransports = [...transports];
    transportsHydrated = true;
  });

  async function createTransport() {
    if (!form.name.trim()) return;
    submitting = true;
    const res = await fetch(`/api/v1/trips/${trip.id}/local_transports`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({
        local_transport: {
          name: form.name,
          transport_type: form.transport_type,
          price_cents: form.price_cents ? Number(form.price_cents) : null,
          balance_cents: form.balance_cents ? Number(form.balance_cents) : null,
          valid_from: form.valid_from || null,
          valid_until: form.valid_until || null,
        },
      }),
    });
    submitting = false;
    if (!res.ok) return;
    const json = await res.json();
    const item = json.data || json;
    localTransports = [...localTransports, item];
    showForm = false;
    form = { name: "", transport_type: "ic_card", price_cents: "", balance_cents: "", valid_from: "", valid_until: "" };
  }

  async function togglePurchased(id: string) {
    const res = await fetch(`/api/v1/trips/${trip.id}/local_transports/${id}/toggle_purchased`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
    });
    if (!res.ok) return;
    localTransports = localTransports.map((t) =>
      t.id === id ? { ...t, purchased: !t.purchased } : t
    );
  }

  async function updateBalance() {
    if (!balanceItem) return;
    submitting = true;
    const res = await fetch(`/api/v1/trips/${trip.id}/local_transports/${balanceItem.id}/update_balance`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ balance_cents: Number(newBalance) }),
    });
    submitting = false;
    if (!res.ok) return;
    localTransports = localTransports.map((t) =>
      t.id === balanceItem!.id ? { ...t, balance_cents: Number(newBalance) } : t
    );
    balanceItem = null;
    newBalance = "";
  }

  function askDelete(id: string) {
    confirmDeleteId = id;
    confirmOpen = true;
  }

  async function deleteTransport() {
    if (!confirmDeleteId) return;
    await fetch(`/api/v1/trips/${trip.id}/local_transports/${confirmDeleteId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken() },
    });
    localTransports = localTransports.filter((t) => t.id !== confirmDeleteId);
    confirmDeleteId = null;
  }

  function openBalanceModal(item: LocalTransport) {
    balanceItem = item;
    newBalance = String(item.balance_cents ?? "");
  }
</script>

<TripSubNav {trip} currentPage="local-transport" />

<div class="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
  <!-- Header -->
  <div class="flex items-center justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold text-white">현지 교통권</h1>
      <p class="mt-1 text-sm text-muted">IC 카드, 패스, 티켓 잔액과 유효기간을 관리합니다.</p>
    </div>
    <Button variant="primary" size="sm" onclick={() => (showForm = true)}>+ 교통권 추가</Button>
  </div>

  <!-- Planned (to purchase) -->
  <section class="space-y-3">
    <h2 class="text-sm font-semibold uppercase tracking-wider text-muted">구매 예정 ({plannedItems.length})</h2>
    {#if plannedItems.length === 0}
      <EmptyState icon="🎫" title="구매 예정 없음" description="구매할 교통권을 추가해보세요." />
    {:else}
      {#each plannedItems as item (item.id)}
        <article class="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
          <div class="flex items-center gap-4">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <p class="font-semibold text-white">{item.name}</p>
                <span class="rounded-full bg-white/8 px-2 py-0.5 text-xs text-muted">{TYPE_LABELS[item.transport_type] ?? item.transport_type}</span>
              </div>
              <p class="mt-1 text-sm text-muted">
                {item.valid_from || "시작일 미정"} ~ {item.valid_until || "종료일 미정"}
              </p>
              {#if item.price_cents}
                <p class="text-sm text-muted">{formatCurrency(item.price_cents, item.currency || "KRW")}</p>
              {/if}
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <Button variant="secondary" size="sm" onclick={() => togglePurchased(item.id)}>구매 완료</Button>
              <button type="button" class="text-xs text-rose-400 hover:text-rose-300" onclick={() => askDelete(item.id)}>삭제</button>
            </div>
          </div>
        </article>
      {/each}
    {/if}
  </section>

  <!-- Owned -->
  <section class="space-y-3">
    <h2 class="text-sm font-semibold uppercase tracking-wider text-muted">보유 중 ({purchasedItems.length})</h2>
    {#each purchasedItems as item (item.id)}
      <article class="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div class="flex items-center gap-4">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <p class="font-semibold text-white">{item.name}</p>
              <span class="rounded-full bg-white/8 px-2 py-0.5 text-xs text-muted">{TYPE_LABELS[item.transport_type] ?? item.transport_type}</span>
              {#if item.is_active_today}
                <span class="rounded-full bg-emerald-400/15 px-2 py-0.5 text-xs text-emerald-300">사용 가능</span>
              {/if}
            </div>
            <div class="mt-1 flex items-center gap-3">
              <p class="text-sm text-muted">
                잔액 <span class="font-medium text-white">{item.balance_cents != null ? formatCurrency(item.balance_cents, item.currency || "KRW") : "-"}</span>
              </p>
              <button
                type="button"
                class="text-xs text-teal-400 hover:text-teal-300 transition-colors"
                onclick={() => openBalanceModal(item)}
              >잔액 수정</button>
            </div>
            {#if item.valid_until}
              <p class="text-xs text-muted mt-0.5">유효기간 ~{item.valid_until}</p>
            {/if}
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <button type="button" class="text-xs text-muted hover:text-secondary" onclick={() => togglePurchased(item.id)}>되돌리기</button>
            <button type="button" class="text-xs text-rose-400 hover:text-rose-300" onclick={() => askDelete(item.id)}>삭제</button>
          </div>
        </div>
      </article>
    {/each}
  </section>
</div>

<!-- Add Form Modal -->
{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
    <div class="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-950 p-6">
      <div class="mb-5 flex items-center justify-between">
        <h2 class="text-lg font-semibold">현지 교통권 추가</h2>
        <button type="button" class="text-muted hover:text-primary" onclick={() => (showForm = false)}>✕</button>
      </div>
      <div class="space-y-3">
        <input bind:value={form.name} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="이름 *" />
        <div>
          <label for="local-transport-type" class="text-xs text-muted mb-1 block">교통권 유형</label>
          <select id="local-transport-type" bind:value={form.transport_type} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white">
            {#each Object.entries(TYPE_LABELS) as [val, lbl]}
              <option value={val}>{lbl}</option>
            {/each}
          </select>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="local-transport-price" class="text-xs text-muted mb-1 block">구매 금액</label>
            <input id="local-transport-price" bind:value={form.price_cents} type="number" min="0" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="원" />
          </div>
          <div>
            <label for="local-transport-balance" class="text-xs text-muted mb-1 block">초기 잔액</label>
            <input id="local-transport-balance" bind:value={form.balance_cents} type="number" min="0" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400" placeholder="원" />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="local-transport-valid-from" class="text-xs text-muted mb-1 block">유효 시작일</label>
            <input id="local-transport-valid-from" bind:value={form.valid_from} type="date" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400" />
          </div>
          <div>
            <label for="local-transport-valid-until" class="text-xs text-muted mb-1 block">유효 종료일</label>
            <input id="local-transport-valid-until" bind:value={form.valid_until} type="date" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400" />
          </div>
        </div>
        <Button variant="primary" fullWidth loading={submitting} onclick={createTransport}>저장</Button>
      </div>
    </div>
  </div>
{/if}

<!-- Balance Update Modal -->
{#if balanceItem}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
    <div class="w-full max-w-sm rounded-3xl border border-white/10 bg-slate-950 p-6">
      <div class="mb-4 flex items-center justify-between">
        <h2 class="text-base font-semibold">잔액 수정 — {balanceItem.name}</h2>
        <button type="button" class="text-muted hover:text-primary" onclick={() => (balanceItem = null)}>✕</button>
      </div>
      <div class="space-y-3">
        <input
          bind:value={newBalance}
          type="number"
          min="0"
          class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-muted outline-none focus:border-teal-400"
          placeholder="새 잔액 (원)"
        />
        <Button variant="primary" fullWidth loading={submitting} onclick={updateBalance}>저장</Button>
      </div>
    </div>
  </div>
{/if}

<ConfirmDialog
  bind:open={confirmOpen}
  title="교통권 삭제"
  message="이 교통권을 삭제하시겠습니까?"
  confirmLabel="삭제"
  onconfirm={deleteTransport}
/>
